import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import React from "react";

import { Link } from "react-router-dom";
import { RenderWhen } from "../../../../../../components";
import RLCard from "../../../../../../components/card/RLCard";
import useModal from "../../../../../../hooks/useModal";
import { AnalysisStatus } from "../../../../../../lib/enums";
import shortNumberFormat from "../../../../../../tcbase/utils/short_number_format";
import DynamicAnalysisFindingsList from "../../../../../network/components/cards/lists/DynamicAnalysisFindingsList";
import { RLCSMergedReportResponse } from "../../../../@types/rldynamicanalysis/rlcs-responses";
import { COMPONENT_IDS } from "../../../../consts";
import { useLocalSampleSummaryParams } from "../../../../hooks";
import { pathToSS } from "../../../../util";
import { DESCRIPTIONS } from "../../../layout-editor/consts";
import { INITIAL_ANALYSIS_SELECTIONS } from "../../../reanalyze/reanalyze-popup/consts";
import Reanalyze from "../../../reanalyze/reanalyze-popup/Reanalyze";
import { useGetMergedRlcsReport } from "../../../rlcloudsandbox/hooks/useGetMergedRlcsReport";
import {
  RLcsAnalysisResponseData,
  useRlcsAnalysisStatus
} from "../../../rlcloudsandbox/hooks/useRlcsAnalysisStatus";
import { Tooltip } from "../common";
import styles from "../network_references/network-references.module.scss";

interface ContentProps {
  mergedReportData: RLCSMergedReportResponse;
  analysisStatusData?: RLcsAnalysisResponseData;
  rlcsAnalyzeModalToggle: () => void;
  isAnalyzing: boolean;
  isLoading: boolean;
  to?: string;
}

const DEFAULT_REFRESH_RATE = 15 * 1000;

const CardContent: React.FC<ContentProps> = ({
  mergedReportData,
  analysisStatusData,
  rlcsAnalyzeModalToggle,
  isAnalyzing,
  isLoading,
  to
}) => (
  <DynamicAnalysisFindingsList
    to={to}
    rlcsReport={mergedReportData}
    rlcsAnalyzeModalToggle={rlcsAnalyzeModalToggle}
    analysisStatusData={analysisStatusData}
    isAnalyzing={isAnalyzing}
    isLoading={isLoading}
  />
);
const DynamicAnalysisInsightsCard = () => {
  const queryClient = useQueryClient();

  const {
    hash = "",
    sourceId = "",
    relationshipId
  } = useLocalSampleSummaryParams();
  const [showRlcsAnalyzeModal, _, rlcsAnalyzeModalToggle] = useModal();

  const { mergedReportData, isLoadingMerged, refetchMergedReport } =
    useGetMergedRlcsReport(hash);

  const { analysisStatusData, refetchAnalysisStatus, analysisStatusQueryKey } =
    useRlcsAnalysisStatus({
      hash,
      refetchMergedReportFn: refetchMergedReport,
      pollDisabled: false
    });

  const onRenalyzeRlcsReport = () => {
    queryClient.setQueryData(analysisStatusQueryKey, {
      analyzing: true,
      analysis_status: "Fetching analysis data...",
      display_status: AnalysisStatus.QUEUED
    });
    setTimeout(() => {
      refetchAnalysisStatus();
    }, DEFAULT_REFRESH_RATE); // analysis status takes some time to update
  };

  const { automated, interactive } = analysisStatusData ?? {};
  const isAnalyzing = automated?.analyzing || interactive?.analyzing;
  const signaturesCount = mergedReportData?.signatures?.length ?? 0;

  return (
    <>
      <RLCard
        header={
          <RLCard.LinkHeader
            title={
              <Tooltip
                placement="top"
                message={
                  <div>
                    <span>
                      {DESCRIPTIONS[COMPONENT_IDS.DYNAMIC_ANALYSIS_INSIGHTS]}
                    </span>
                    <br />
                    <span>
                      Total number of RL Cloud Sandbox Signatures:{" "}
                      {shortNumberFormat(signaturesCount)}.
                    </span>
                  </div>
                }
              >
                <div className="flex flex-gap-2">
                  <span data-testid="report_summary_dynamic_analysis_insights_title">
                    {`Dynamic Analysis Insights (${signaturesCount})`}
                  </span>
                </div>
              </Tooltip>
            }
            LinkComponent={
              <RenderWhen condition={Boolean(mergedReportData)}>
                <Link
                  className={styles.link}
                  to={`${pathToSS(
                    hash,
                    sourceId,
                    "rl_dynamic_analysis/signatures/",
                    relationshipId
                  )}#dynamic-analysis-tabs`}
                  data-testid="report_summary_dynamic_analysis_insights_see_all_link"
                >
                  See All Signatures
                </Link>
              </RenderWhen>
            }
          />
        }
        content={
          <RLCard.Content
            className={clsx(
              "flex justify-content-center min-400 flex-gap-2",
              !mergedReportData ? "flex-center" : ""
            )}
            data-testid="report_summary_dynamic_analysis_insights_grid"
          >
            <CardContent
              to={`${pathToSS(
                hash,
                sourceId,
                "rl_dynamic_analysis/signatures/",
                relationshipId
              )}#dynamic-analysis-tabs`}
              mergedReportData={mergedReportData}
              isAnalyzing={!!isAnalyzing}
              isLoading={isLoadingMerged}
              rlcsAnalyzeModalToggle={rlcsAnalyzeModalToggle}
              analysisStatusData={analysisStatusData}
            />
          </RLCard.Content>
        }
        data-testid="report_summary_dynamic_analysis_insights_card"
      />

      <RenderWhen condition={showRlcsAnalyzeModal}>
        <Reanalyze
          samples={[
            {
              hash_value: hash,
              source_id: sourceId,
              relationship_id: relationshipId
            }
          ]}
          onClose={rlcsAnalyzeModalToggle}
          callbackOnSuccess={onRenalyzeRlcsReport}
          initial={{
            ...INITIAL_ANALYSIS_SELECTIONS,
            rl_dynamic_analysis: !automated?.analyzing,
            rl_dynamic_analysis_interactive: !interactive?.analyzing
          }}
        />
      </RenderWhen>
    </>
  );
};

export default DynamicAnalysisInsightsCard;
